import { Button, Card, Input, Modal, Popconfirm, Select, Tag, message } from 'antd';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { CiSquareMinus, CiSquarePlus } from "react-icons/ci";
import { FiEdit2 } from "react-icons/fi";
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { getBaseUrl } from '../../../../config/base-url';
import { useListDistrict, useListState } from '../../../requests/address';
import {
    useCourierProviders,
    useProductQtyAdjustOrderItem,
    useRefreshCourierStatus,
    useRefreshOrderCourierStatus,
    useRemoveOrderItem,
    useSendOrderToCourier,
    useSendPackageToCourier,
    useUpdateOrder,
    useUpdateOrderProducts,
} from '../../../requests/order';
import { useAllProductList } from '../../../requests/products';
import { route } from '../../../requests/routes';
import useWindowSize from '../../../utils/useWindowDimension';

// Single source of truth for refreshing the order after a mutation, so we never
// have to fall back to a full page reload. Refetches the V4 query that drives
// this view and triggers the parent's loadData() so the summary sidebar
// (invoice total, remaining amount) stays in sync too.
function useRefreshOrder(loadData) {
    const queryClient = useQueryClient();
    return React.useCallback(() => {
        queryClient.invalidateQueries([route.ORDER_DETAILS_V4]);
        loadData?.();
    }, [queryClient, loadData]);
}

const MultiVendorOrderDetails = ({ data: order, loadData }) => {
    const refresh = useRefreshOrder(loadData);

    return (
        <div>
            <Card
                title={`#${order?.id}`}
                extra={[
                    <Button
                        key="full-invoice"
                        onClick={() => window.open(`${getBaseUrl()}/api/orders/single-invoice-print/${order?.id}`)}
                        style={{ marginRight: 10 }}
                    >
                        Full Invoice
                    </Button>,
                    <span key="date">{moment(order?.date).format('DD MMM hh:mm:ss A')}</span>,
                ]}
            >
                <ShippingAddressCard order={order} refresh={refresh} />

                <OrderCourier order={order} refresh={refresh} />

                {order?.packages?.map((pkg, index) => (
                    <Package key={pkg?.id ?? index} pkg={pkg} orderId={order?.id} index={index + 1} refresh={refresh} />
                ))}

                <AddProductCard orderId={order?.id} refresh={refresh} />
            </Card>
        </div>
    );
};

export default MultiVendorOrderDetails;

/* -------------------------------------------------------------------------- */
/*                              Shipping Address                               */
/* -------------------------------------------------------------------------- */

function ShippingAddressCard({ order, refresh }) {
    const { user, customer, note } = order || {};
    const { width } = useWindowSize();
    const isMobile = width < 768;

    const [editOpen, setEditOpen] = useState(false);

    return (
        <Card
            style={{ marginBottom: 10 }}
            bodyStyle={{ position: 'relative' }}
        >
            <Button
                size="small"
                icon={<FiEdit2 />}
                onClick={() => setEditOpen(true)}
                style={{ position: 'absolute', top: 10, right: 10, display: 'flex', alignItems: 'center', gap: 5 }}
            >
                Edit
            </Button>

            <div style={isMobile ? { display: 'flex', flexDirection: 'column-reverse' } : { display: 'flex' }}>
                <div style={{ padding: 10, flex: 1 }}>
                    <label style={{ fontSize: 20 }}>Shipping Address</label>
                    {/* These come from the order's own shipping snapshot (order.user),
                        NOT the customer account — that is the bug we are fixing. */}
                    <p style={{ padding: 0, marginBlockStart: 0, marginBlockEnd: 0 }}>{user?.full_name}</p>
                    <p style={{ padding: 0, marginBlockStart: 0, marginBlockEnd: 0 }}>{user?.phone}</p>
                    <p style={{ padding: 0, marginBlockStart: 0, marginBlockEnd: 0 }}>
                        {[user?.address, user?.area, user?.city, user?.province].filter(Boolean).join(', ')}
                    </p>
                    {note && (
                        <p style={{ padding: 0, marginBlockStart: 8, marginBlockEnd: 0 }}>
                            <strong>Order Note:</strong> {note}
                        </p>
                    )}
                </div>

                <div style={{ padding: 10, display: 'flex', justifyContent: 'flex-start', flex: 1 }}>
                    <Link to={`/customer/${customer?.id}`}>
                        <img style={{ width: 50, height: 'auto' }} src={customer?.image ?? 'assets/user.png'} alt="" />
                    </Link>
                    <div style={{ paddingLeft: 10, display: 'flex', flexDirection: 'column' }}>
                        <Link style={{ cursor: 'pointer' }} to={`/customer/${customer?.id}`}>
                            <label style={{ fontSize: 20 }}>{customer?.first_name} {customer?.last_name}</label>
                        </Link>
                        <p style={{ padding: 0, marginBlockStart: 0, marginBlockEnd: 0 }}>{customer?.phone}</p>
                        <p style={{ padding: 0, marginBlockStart: 0, marginBlockEnd: 0 }}>{customer?.email}</p>
                    </div>
                </div>
            </div>

            <EditShippingAddressModal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                orderId={order?.id}
                user={user}
                refresh={refresh}
            />
        </Card>
    );
}

function EditShippingAddressModal({ open, onClose, orderId, user, refresh }) {
    const { mutate: updateOrder, isLoading: saving } = useUpdateOrder();
    const [form, setForm] = useState({});

    // Province / district come from the same shipping config the client billing
    // page uses, so the options here mirror localhost:3000. Area is free text.
    const { data: states } = useListState();
    const { data: districts } = useListDistrict();

    // Reseed the form from the latest order data each time the modal opens.
    React.useEffect(() => {
        if (open) {
            setForm({
                full_name: user?.full_name ?? '',
                phone: user?.phone ?? '',
                address: user?.address ?? '',
                province: user?.province ?? '',
                city: user?.city ?? '',
                area: user?.area ?? '',
            });
        }
    }, [open, user]);

    // Cascade: districts belong to the selected state.
    const selectedState = useMemo(
        () => (states || []).find((s) => String(s.name).toLowerCase() === String(form.province).toLowerCase()),
        [states, form.province]
    );

    const provinceOptions = (states || []).map((s) => ({ value: s.name, label: s.name }));
    const cityOptions = (districts || [])
        .filter((d) => selectedState && d.stateId === selectedState.id)
        .map((d) => ({ value: d.name, label: d.name }));

    function submit() {
        if (!form.full_name?.trim()) {
            message.error('Name is required');
            return;
        }
        updateOrder({ id: orderId, ...form }, {
            onSuccess: () => {
                message.success('Shipping address updated');
                refresh();
                onClose();
            },
            onError: (err) => message.error(err?.message || 'Failed to update shipping address'),
        });
    }

    const labelStyle = { display: 'block', marginBottom: 4, fontWeight: 500 };

    return (
        <Modal
            title="Edit Shipping Address"
            open={open}
            onCancel={onClose}
            onOk={submit}
            okText="Save"
            confirmLoading={saving}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Input
                    addonBefore="Name"
                    value={form.full_name ?? ''}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                />
                <Input
                    addonBefore="Phone"
                    value={form.phone ?? ''}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
                <Input
                    addonBefore="Address"
                    value={form.address ?? ''}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                />

                <div>
                    <label style={labelStyle}>Division</label>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Choose Division"
                        value={form.province || undefined}
                        options={provinceOptions}
                        onChange={(value) => setForm({ ...form, province: value, city: '', area: '' })}
                        showSearch
                        optionFilterProp="label"
                    />
                </div>

                <div>
                    <label style={labelStyle}>District</label>
                    <Select
                        style={{ width: '100%' }}
                        placeholder={form.province ? 'Choose District' : 'Pick division first'}
                        value={form.city || undefined}
                        options={cityOptions}
                        disabled={!form.province}
                        onChange={(value) => setForm({ ...form, city: value, area: '' })}
                        showSearch
                        optionFilterProp="label"
                    />
                </div>

                <Input
                    addonBefore="Area"
                    value={form.area ?? ''}
                    onChange={(e) => setForm({ ...form, area: e.target.value })}
                />
            </div>
        </Modal>
    );
}

/* -------------------------------------------------------------------------- */
/*                       Send All Packages to Courier                          */
/* -------------------------------------------------------------------------- */

// Sends every package/product on the order to the courier as a single
// consignment (one COD amount, full delivery charge included), as an
// alternative to sending each seller's package separately below.
function OrderCourier({ order, refresh }) {
    const { data: providersData } = useCourierProviders();
    const providers = providersData?.providers || [];
    const [selectedCourier, setSelectedCourier] = useState(providers?.[0]?.name);

    const { mutate: sendToCourier, isLoading: sending } = useSendOrderToCourier();
    const { mutate: refreshStatus, isLoading: refreshing } = useRefreshOrderCourierStatus();

    const shipment = order?.courier_shipments?.[0];

    function send() {
        const courier = shipment?.courier || selectedCourier || providers?.[0]?.name;
        if (!courier) return;
        sendToCourier(
            { order_id: order?.id, courier },
            {
                onSuccess: (data) => {
                    if (data?.status === 200) {
                        message.success(data?.message || 'Sent to courier');
                    } else {
                        message.error(data?.message || 'Failed to send to courier');
                    }
                    refresh();
                },
                onError: (err) => message.error(err?.response?.data?.message || err?.message || 'Failed to send to courier'),
            }
        );
    }

    function refreshCourierStatus() {
        refreshStatus(
            { order_id: order?.id, courier: shipment?.courier },
            {
                onSuccess: () => {
                    message.success('Status refreshed');
                    refresh();
                },
                onError: (err) => message.error(err?.response?.data?.message || err?.message || 'Failed to refresh status'),
            }
        );
    }

    return (
        <Card style={{ marginBottom: 10 }} title="Send All to Courier">
            {shipment ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color={shipment.status === 'failed' ? 'red' : 'blue'}>{shipment.courier}</Tag>
                    <span>Tracking: {shipment.tracking_code || '—'}</span>
                    <Tag>{shipment.status}</Tag>
                    {shipment.status === 'failed' && (
                        <Button size="small" danger onClick={send} loading={sending}>
                            Retry Send
                        </Button>
                    )}
                    {shipment.consignment_id && (
                        <Button size="small" onClick={refreshCourierStatus} loading={refreshing}>
                            Refresh Status
                        </Button>
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Select
                        style={{ width: 180 }}
                        placeholder="Select courier"
                        value={selectedCourier}
                        onChange={setSelectedCourier}
                        options={providers.map((p) => ({ label: p.label, value: p.name }))}
                    />
                    <Button type="primary" onClick={send} loading={sending} disabled={!selectedCourier}>
                        Send All to Courier
                    </Button>
                </div>
            )}
        </Card>
    );
}

/* -------------------------------------------------------------------------- */
/*                                  Packages                                   */
/* -------------------------------------------------------------------------- */

function Package({ pkg, orderId, index, refresh }) {
    return (
        <Card
            style={{ marginBottom: 10 }}
            title={<span>PACKAGE {index}</span>}
            extra={
                <div style={{ display: 'flex' }}>
                    <Link
                        style={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}
                        to={`/seller_detail/${pkg?.seller?.id}`}
                    >
                        <img width={30} height={30} src={pkg?.seller?.logo} alt="" />
                        <span style={{ fontWeight: 'bold', paddingLeft: 5 }}>{pkg?.seller?.shop_name}</span>
                    </Link>

                    <Button
                        onClick={() => window.open(`${getBaseUrl()}/api/orders/single-invoice-print/${orderId}?package=${pkg?.id}`)}
                        style={{ marginLeft: 5 }}
                    >
                        invoice
                    </Button>
                </div>
            }
        >
            {pkg?.products?.map((product) => (
                <Product key={product?.product_id} product={product} orderId={orderId} refresh={refresh} />
            ))}

            <PackageCourier pkg={pkg} refresh={refresh} />
        </Card>
    );
}

function PackageCourier({ pkg, refresh }) {
    const { data: providersData } = useCourierProviders();
    const providers = providersData?.providers || [];
    const [selectedCourier, setSelectedCourier] = useState(providers?.[0]?.name);

    const { mutate: sendToCourier, isLoading: sending } = useSendPackageToCourier();
    const { mutate: refreshStatus, isLoading: refreshing } = useRefreshCourierStatus();

    // Only one shipment is expected per package today (one courier at a time),
    // but the backend keys by (package, courier) so this stays correct if a
    // package is ever re-sent through a different provider later.
    const shipment = pkg?.courier_shipments?.[0];

    function send() {
        // Once a shipment row exists, the courier picker is hidden and "Send to
        // Courier" becomes "Retry Send" — it must resend to the courier that
        // actually failed, not whatever selectedCourier was left at from before
        // a shipment existed (which defaults to the first provider, Steadfast).
        const courier = shipment?.courier || selectedCourier || providers?.[0]?.name;
        if (!courier) return;
        sendToCourier(
            { package_id: pkg?.id, courier },
            {
                onSuccess: (data) => {
                    if (data?.status === 200) {
                        message.success(data?.message || 'Sent to courier');
                    } else {
                        message.error(data?.message || 'Failed to send to courier');
                    }
                    refresh();
                },
                onError: (err) => message.error(err?.response?.data?.message || err?.message || 'Failed to send to courier'),
            }
        );
    }

    function refreshCourierStatus() {
        refreshStatus(
            { package_id: pkg?.id, courier: shipment?.courier },
            {
                onSuccess: () => {
                    message.success('Status refreshed');
                    refresh();
                },
                onError: (err) => message.error(err?.response?.data?.message || err?.message || 'Failed to refresh status'),
            }
        );
    }

    return (
        <div
            style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            {shipment ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color={shipment.status === 'failed' ? 'red' : 'blue'}>{shipment.courier}</Tag>
                    <span>Tracking: {shipment.tracking_code || '—'}</span>
                    <Tag>{shipment.status}</Tag>
                    {shipment.status === 'failed' && (
                        <Button size="small" danger onClick={send} loading={sending}>
                            Retry Send
                        </Button>
                    )}
                    {shipment.consignment_id && (
                        <Button size="small" onClick={refreshCourierStatus} loading={refreshing}>
                            Refresh Status
                        </Button>
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Select
                        style={{ width: 180 }}
                        placeholder="Select courier"
                        value={selectedCourier}
                        onChange={setSelectedCourier}
                        options={providers.map((p) => ({ label: p.label, value: p.name }))}
                    />
                    <Button type="primary" onClick={send} loading={sending} disabled={!selectedCourier}>
                        Send to Courier
                    </Button>
                </div>
            )}
        </div>
    );
}

function Product({ product, orderId, refresh }) {
    const { mutate: removeProductFromCart } = useRemoveOrderItem();
    const { mutate: productQtyAdjust } = useProductQtyAdjustOrderItem();
    const { mutate: updateOrderProducts, isLoading: savingPrice } = useUpdateOrderProducts();
    const [priceOpen, setPriceOpen] = useState(false);
    const [priceValue, setPriceValue] = useState(0);

    function changeQty(qty) {
        if (qty < 1) return;
        productQtyAdjust(
            { product_id: product?.product_id, order_id: orderId, qty },
            {
                onSuccess: () => {
                    message.success('Quantity updated');
                    refresh();
                },
                onError: (err) => message.error(err?.message || 'Failed to update quantity'),
            }
        );
    }

    function savePrice() {
        updateOrderProducts(
            {
                id: orderId,
                products: [{
                    product_id: product?.product_id,
                    quantity: product?.quantity,
                    price: Number(priceValue),
                    color: product?.color,
                    size: product?.size,
                    price_type: product?.price_type,
                }],
            },
            {
                onSuccess: () => {
                    message.success('Price updated');
                    setPriceOpen(false);
                    refresh();
                },
                onError: (err) => message.error(err?.message || 'Failed to update price'),
            }
        );
    }

    function remove() {
        removeProductFromCart(
            { product_id: product?.product_id, order_id: orderId },
            {
                onSuccess: () => {
                    message.success('Product removed successfully');
                    refresh();
                },
                onError: (err) => message.error(err?.message || 'Failed to remove product'),
            }
        );
    }

    return (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <a href={`/product/detail/${product?.product_id}`}>
                    <img style={{ width: 100, height: 'auto' }} src={product?.image} alt="" />
                </a>

                <Popconfirm
                    title="Are you sure to delete? You can't undo it."
                    onConfirm={remove}
                >
                    <Button>remove</Button>
                </Popconfirm>
            </div>

            <div style={{ flex: 1, display: 'flex', justifyContent: 'start' }}>
                <div style={{ flex: 3, paddingLeft: 10, display: 'flex', flexDirection: 'column' }}>
                    <Link to={`/product/detail/${product?.product_id}`} style={{ padding: 0, marginBlockStart: 0, marginBlockEnd: 0 }}>
                        <span style={{ fontWeight: 'bold' }}>{product?.product_title}</span>
                    </Link>
                    <p style={{ padding: 0, marginBlockStart: 0, marginBlockEnd: 0 }}>
                        {product?.price_type ? (
                            <Tag color={product.price_type === 'official' ? 'blue' : 'gold'}>
                                {product.price_type === 'official' ? 'Official' : 'Unofficial'}
                            </Tag>
                        ) : (
                            <>
                                <span>Size: {product?.size}</span>
                                <span>Color: {product?.color_name || product?.color}</span>
                            </>
                        )}
                    </p>
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span>BDT {product?.price}</span>
                    <Button
                        type="link"
                        size="small"
                        icon={<FiEdit2 />}
                        onClick={() => { setPriceValue(product?.price); setPriceOpen(true); }}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 60, minWidth: 60 }}>
                        <Button
                            onClick={() => changeQty(product?.quantity - 1)}
                            disabled={product?.quantity <= 1}
                            icon={<CiSquareMinus size={25} color="#ccc" />}
                        />
                        <span
                            style={{
                                fontSize: 14,
                                border: '1px solid #ccc',
                                borderRadius: '5px',
                                padding: '2px 10px',
                                margin: '0px 2px',
                            }}
                        >
                            {product?.quantity}
                        </span>
                        <Button
                            onClick={() => changeQty(product?.quantity + 1)}
                            icon={<CiSquarePlus size={25} color="#ccc" />}
                        />
                    </div>
                </div>
            </div>

            <Modal
                title="Edit Product Price"
                open={priceOpen}
                onCancel={() => setPriceOpen(false)}
                onOk={savePrice}
                okText="Save"
                confirmLoading={savingPrice}
            >
                <Input
                    type="number"
                    addonBefore="Price"
                    value={priceValue}
                    onChange={(e) => setPriceValue(e.target.value)}
                />
            </Modal>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*                                Add Product                                  */
/* -------------------------------------------------------------------------- */

const emptyForm = {
    product_id: null,
    product_title: '',
    shop_name: '',
    quantity: 1,
    price: 0,
    color: '',
    size: '',
    has_official_price: false,
    official_price: 0,
    unofficial_price: 0,
    price_type: null,
};

function AddProductCard({ orderId, refresh }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [form, setForm] = useState(emptyForm);

    // NOTE: the backend filters on `searchedValue`, not `search` — passing the
    // wrong key (as the single-vendor view does) silently disables search.
    const { data: allProductsData } = useAllProductList({ searchedValue: search, page: 1, pageSize: 20 });
    const { mutate: updateOrderProducts, isLoading: saving } = useUpdateOrderProducts();

    const allProducts = allProductsData?.all_products || [];

    function openModal() {
        setForm(emptyForm);
        setSearch('');
        setOpen(true);
    }

    function pickProduct(p) {
        setForm({
            product_id: p.id,
            product_title: p.product_title,
            shop_name: p.vendor?.shop_name ?? '',
            quantity: 1,
            // Official/unofficial products have no meaningful price until a
            // side is chosen below, so don't default to price/sale_price for them.
            price: p.has_official_price ? 0 : (p.sale_price > 0 ? p.sale_price : p.price),
            color: p.color && String(p.color).toLowerCase() !== 'n/a' ? p.color : '',
            size: p.size && String(p.size).toLowerCase() !== 'n/a' ? p.size : '',
            has_official_price: !!p.has_official_price,
            official_price: p.official_price || 0,
            unofficial_price: p.unofficial_price || 0,
            price_type: null,
        });
    }

    function submit() {
        if (!form.product_id) {
            message.error('Pick a product first');
            return;
        }
        if (form.has_official_price && !form.price_type) {
            message.error('Choose Official or Unofficial price for this product');
            return;
        }
        const payload = {
            product_id: form.product_id,
            quantity: Number(form.quantity) || 1,
            price: Number(form.price) || 0,
            color: form.color || '',
            size: form.size || '',
            price_type: form.has_official_price ? form.price_type : null,
        };
        updateOrderProducts({ id: orderId, products: [payload] }, {
            onSuccess: () => {
                message.success('Product added to order');
                refresh();
                setOpen(false);
            },
            onError: (err) => message.error(err?.message || 'Failed to add product'),
        });
    }

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <Button type="primary" onClick={openModal}>+ Add Product</Button>
            </div>

            <Modal
                title="Add Product to Order"
                open={open}
                onCancel={() => setOpen(false)}
                onOk={submit}
                okText="Add"
                confirmLoading={saving}
                width={700}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Input
                        placeholder="Search products..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4, padding: 8 }}>
                        {allProducts.length === 0 ? (
                            <div style={{ padding: 12, color: '#999', textAlign: 'center' }}>No products found</div>
                        ) : allProducts.map((p) => (
                            <div
                                key={p.id}
                                onClick={() => pickProduct(p)}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    background: form.product_id === p.id ? '#e6f7ff' : 'transparent',
                                    borderRadius: 4,
                                }}
                            >
                                <span>{p.product_title}</span>
                                {p.vendor?.shop_name && <Tag color="geekblue">{p.vendor.shop_name}</Tag>}
                            </div>
                        ))}
                    </div>

                    {form.product_id && (
                        <>
                            <Tag color="blue">
                                {form.product_title}
                                {form.shop_name ? ` — ${form.shop_name}` : ''}
                            </Tag>
                            <Input addonBefore="Quantity" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                            {form.has_official_price ? (
                                <Select
                                    placeholder="Choose Official or Unofficial price"
                                    value={form.price_type || undefined}
                                    onChange={(val) => setForm({
                                        ...form,
                                        price_type: val,
                                        price: val === 'official' ? form.official_price : form.unofficial_price,
                                    })}
                                    options={[
                                        { label: `Official — BDT ${form.official_price}`, value: 'official' },
                                        { label: `Unofficial — BDT ${form.unofficial_price}`, value: 'unofficial' },
                                    ]}
                                />
                            ) : (
                                <Input addonBefore="Price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                            )}
                            <Input addonBefore="Color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
                            <Input addonBefore="Size" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} />
                        </>
                    )}
                </div>
            </Modal>
        </>
    );
}
